import type { FormEvent } from 'react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { CopySimple as CopyIcon } from '@phosphor-icons/react/dist/ssr/CopySimple'
import { Info as InfoIcon } from '@phosphor-icons/react/dist/ssr/Info'
import { Plus as PlusIcon } from '@phosphor-icons/react/dist/ssr/Plus'
import { Trash as TrashIcon } from '@phosphor-icons/react/dist/ssr/Trash'
import { X as XIcon } from '@phosphor-icons/react/dist/ssr/X'
import { useLocation, useMatches } from 'react-router-dom'

import { getPageContextScopeKey, getPageNameFromMatches, isEntityPageContext } from '@/lib/assistant-page-context'
import { getPinnedPrompts, pinPrompt, unpinPrompt, type PinnedPrompt } from '@/lib/assistant-pinned-prompts'
import { getFollowUpSuggestions, getSuggestedPrompts } from '@/lib/assistant-prompts'
import { speakAssistantText, stopAssistantSpeech, useAssistantSpeechState } from '@/lib/assistant-speech'
import { track } from '@/lib/segment'
import { useChat } from '@/contexts/chat-context'
import {
	buildAssistantContextLabel,
	buildCurrentPageContext,
	useAssistantContextLabels,
} from '@/hooks/use-assistant-context-labels'
import { useHideHubspotWhileMounted } from '@/hooks/use-hubspot-chat-visibility'
import { useSpeechRecognition } from '@/hooks/use-speech-recognition'
import { useUser } from '@/hooks/use-user'

import { AssistantEmblem } from './assistant-emblem'
import { ChatComposer } from './chat-composer'
import { ChatPanelHeader } from './chat-header'
import { ChatHistoryList } from './chat-history'
import { ChatMessageBubble } from './chat-message'
import { PromptRow, promptTextSx } from './chat-prompt-row'
import {
	ASSISTANT_CLOSE_MS,
	ASSISTANT_MOTION_EASE,
	ASSISTANT_OPEN_MS,
	ASSISTANT_PANEL_WIDTH,
	ASSISTANT_SCALE,
	ASSISTANT_WINDOW_HEIGHT,
	ASSISTANT_WINDOW_OFFSET,
	useSideModalDock,
} from './chat-window'
import { ThinkingIndicator } from './thinking-indicator'

export function ChatPanel() {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
	const location = useLocation()
	const matches = useMatches()
	const pageName = getPageNameFromMatches(matches)
	const { user } = useUser()
	const firstName = user?.name?.trim().split(/\s+/)[0]
	const {
		isOpen,
		messages,
		isLoading,
		isLoadingHistory,
		error,
		sendMessage,
		clearThread,
		selectThread,
		deleteThread,
		refreshThreads,
		threadId,
		threads,
		hasAccess,
		closePanel,
		openPanel,
		beginDraftConversation,
		cancelDraftConversation,
		isDraftConversation,
		stopStreaming,
		includePageContext,
		togglePageContext,
	} = useChat()
	const contextLabels = useAssistantContextLabels(location.pathname, location.search, hasAccess)
	const contextLabel = buildAssistantContextLabel(location.pathname, pageName, contextLabels, location.search)
	const currentPageContext = buildCurrentPageContext(location.pathname, pageName, contextLabels, location.search)
	const currentScopeKey = getPageContextScopeKey(currentPageContext)
	// Show the strip whenever the screen can be labeled — hidden on mobile
	// (context still attaches silently there).
	const showContextLabel = Boolean(contextLabel) && !isMobile && includePageContext
	const suggestedPrompts = getSuggestedPrompts(location.pathname, location.search)
	const lastAssistantMsg = [...messages].reverse().find((m) => m.role === 'assistant' && !m.isStreaming)
	const followUps =
		!isLoading && lastAssistantMsg
			? getFollowUpSuggestions(lastAssistantMsg.content, location.pathname, lastAssistantMsg.toolCalls)
			: []
	const [input, setInput] = useState('')
	const [isExpanded, setIsExpanded] = useState(false)
	const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)
	const [showHistory, setShowHistory] = useState(false)
	const [threadMenu, setThreadMenu] = useState<{ anchorEl: HTMLElement; threadId: string } | null>(null)
	const [threadToDelete, setThreadToDelete] = useState<string | null>(null)
	const [isDeletingThread, setIsDeletingThread] = useState(false)
	const [pinnedPrompts, setPinnedPrompts] = useState<PinnedPrompt[]>(() => getPinnedPrompts())
	const [dismissedNavNudgeKey, setDismissedNavNudgeKey] = useState<string | null>(null)
	const bottomRef = useRef<HTMLDivElement>(null)
	const inputRef = useRef<HTMLTextAreaElement>(null)
	const listRef = useRef<HTMLDivElement>(null)
	const isNearBottomRef = useRef(true)
	const scrollingToBottomRef = useRef(false)
	const [showScrollDown, setShowScrollDown] = useState(false)

	const { dockedRight, panelZIndex, sideModalDocked } = useSideModalDock(location.search, isExpanded)

	const lastUserWithEntity = [...messages]
		.reverse()
		.find((message) => message.role === 'user' && message.pageContext && isEntityPageContext(message.pageContext))
	const lastEntityScopeKey = lastUserWithEntity?.pageContext
		? getPageContextScopeKey(lastUserWithEntity.pageContext)
		: null
	const navNudgeKey = threadId && lastEntityScopeKey ? `${threadId}:${lastEntityScopeKey}` : null
	const showNavNudge = Boolean(
		navNudgeKey && lastEntityScopeKey !== currentScopeKey && dismissedNavNudgeKey !== navNudgeKey
	)

	useEffect(() => {
		setDismissedNavNudgeKey(null)
	}, [threadId])

	useEffect(() => {
		if (showHistory) {
			void refreshThreads()
		}
	}, [showHistory, refreshThreads])

	useHideHubspotWhileMounted(isOpen && hasAccess)

	const { isSpeaking: isSpeechPlaying, isLoading: isSpeechLoading } = useAssistantSpeechState()
	const isReadingAloud = isSpeechPlaying || isSpeechLoading

	const lastInputWasVoiceRef = useRef(false)
	const pendingSpeechInputRef = useRef(false)

	const handleInputChange = useCallback((value: string) => {
		setInput(value)
		if (!value.trim()) {
			pendingSpeechInputRef.current = false
		}
	}, [])

	const handleSpeechResult = useCallback((text: string) => {
		pendingSpeechInputRef.current = true
		setInput((prev) => (prev ? `${prev} ${text}` : text))
		void track('AssistantVoiceUsed')
	}, [])

	const handleVoiceAutoSend = useCallback(
		(text: string) => {
			if (!isLoading) {
				lastInputWasVoiceRef.current = true
				pendingSpeechInputRef.current = false
				void sendMessage(text, { inputMethod: 'voice' })
				setInput('')
			}
		},
		[isLoading, sendMessage]
	)

	useEffect(() => {
		if (!lastInputWasVoiceRef.current || !lastAssistantMsg || isLoading) return
		lastInputWasVoiceRef.current = false
		void speakAssistantText(lastAssistantMsg.content ?? '', { messageId: lastAssistantMsg.id }).catch(() => {
			// TTS is optional — fail silently
		})
	}, [lastAssistantMsg, isLoading])

	useEffect(() => {
		if (!isOpen) {
			stopAssistantSpeech()
		}
	}, [isOpen])

	// Reset to the floating window after the close transition finishes, so
	// reopening always starts compact instead of animating a resize mid-close.
	useEffect(() => {
		if (isOpen) return
		const timer = setTimeout(() => {
			setIsExpanded(false)
		}, ASSISTANT_CLOSE_MS)
		return () => {
			clearTimeout(timer)
		}
	}, [isOpen])

	const handleToggleExpanded = useCallback(() => {
		setIsExpanded((prev) => {
			void track('AssistantFullscreenToggled', { expanded: !prev })
			return !prev
		})
	}, [])

	const {
		isListening,
		isSupported,
		interimText,
		error: micError,
		toggleListening,
		clearError,
	} = useSpeechRecognition(handleSpeechResult, handleVoiceAutoSend)

	const handleToggleListening = useCallback(() => {
		if (isReadingAloud) {
			stopAssistantSpeech()
		}
		toggleListening()
	}, [isReadingAloud, toggleListening])

	const scrollToBottom = useCallback(() => {
		// Hide immediately and stay hidden while the smooth scroll travels —
		// otherwise each scroll event re-shows the button mid-flight.
		scrollingToBottomRef.current = true
		isNearBottomRef.current = true
		setShowScrollDown(false)
		bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
	}, [])

	const updateScrollIndicators = useCallback(() => {
		const el = listRef.current
		if (!el) return
		const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120
		if (nearBottom) scrollingToBottomRef.current = false
		isNearBottomRef.current = nearBottom || scrollingToBottomRef.current
		setShowScrollDown(!nearBottom && !scrollingToBottomRef.current)
	}, [])

	const handleListScroll = useCallback(() => {
		updateScrollIndicators()
	}, [updateScrollIndicators])

	// Scroll events alone miss cases where content changes without a scroll
	// (opening a long thread at the top, appends that don't move the view) —
	// re-check whenever messages or loading state change so the pill is accurate.
	useEffect(() => {
		updateScrollIndicators()
	}, [messages, isLoading, updateScrollIndicators])

	const lastMessageCountRef = useRef(0)
	// Set on send — the next append is a question we should pin to the top
	// of the viewport so the incoming answer fills the space below it.
	// (sendMessage appends user + assistant placeholder in one update, so
	// we look for the last user message rather than the last message.)
	const scrollOnSendRef = useRef(false)

	// Land at the latest message when a thread finishes loading — before
	// paint so a long history never flashes its top.
	const wasLoadingHistoryRef = useRef(false)
	useLayoutEffect(() => {
		const el = listRef.current
		if (wasLoadingHistoryRef.current && !isLoadingHistory && el && messages.length) {
			el.scrollTop = el.scrollHeight
			isNearBottomRef.current = true
			setShowScrollDown(false)
		}
		wasLoadingHistoryRef.current = isLoadingHistory
	}, [isLoadingHistory, messages])

	// Layout effect so the pin and the entrance class land before paint —
	// no first-frame flash of the bubble at its resting position.
	useLayoutEffect(() => {
		const appended = messages.length > lastMessageCountRef.current
		lastMessageCountRef.current = messages.length
		if (!appended || !scrollOnSendRef.current) return
		const lastUser = [...messages].reverse().find((m) => m.role === 'user')
		if (!lastUser) return
		scrollOnSendRef.current = false
		const container = listRef.current
		const el = container?.querySelector<HTMLElement>(`[data-mid="${lastUser.id}"]`)
		if (!container || !el) return
		el.classList.add('msg-sent')
		container.scrollTop =
			el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 28
	}, [messages])

	const handleSubmit = useCallback(
		async (event?: FormEvent) => {
			event?.preventDefault()
			const trimmed = input.trim()
			if (!trimmed || isLoading) return
			if (isMobile) inputRef.current?.blur()
			const inputMethod = pendingSpeechInputRef.current ? 'voice' : 'text'
			pendingSpeechInputRef.current = false
			if (inputMethod === 'voice') {
				lastInputWasVoiceRef.current = true
			}
			setInput('')
			isNearBottomRef.current = true
			scrollOnSendRef.current = true
			setShowScrollDown(false)
			await sendMessage(trimmed, { inputMethod })
		},
		[input, isLoading, sendMessage, isMobile]
	)

	const handleSuggestedPrompt = useCallback(
		(prompt: string) => {
			if (isLoading) return
			scrollOnSendRef.current = true
			void sendMessage(prompt)
		},
		[isLoading, sendMessage]
	)

	const handlePinPrompt = useCallback((prompt: string) => {
		setPinnedPrompts(pinPrompt(prompt))
	}, [])

	const handleUnpinPrompt = useCallback((id: string) => {
		setPinnedPrompts(unpinPrompt(id))
	}, [])

	const handleConfirmDeleteThread = useCallback(async () => {
		if (!threadToDelete) return
		setIsDeletingThread(true)
		try {
			await deleteThread(threadToDelete)
			setThreadToDelete(null)
		} finally {
			setIsDeletingThread(false)
		}
	}, [deleteThread, threadToDelete])

	const handleCopyMarkdown = useCallback(() => {
		const markdown = messages
			.filter((message) => message.content)
			.map((message) => `**${message.role === 'user' ? 'You' : 'Assistant'}:**\n\n${message.content}`)
			.join('\n\n---\n\n')
		if (!markdown) return
		void navigator.clipboard.writeText(markdown).catch(() => undefined)
		void track('AssistantChatCopied', { threadId })
	}, [messages, threadId])

	const handleClosePanel = useCallback(() => {
		closePanel()
		if (!isDraftConversation) beginDraftConversation()
	}, [closePanel, isDraftConversation, beginDraftConversation])

	if (!hasAccess) return null

	const panelMotion = [
		`transform ${isOpen ? ASSISTANT_OPEN_MS : ASSISTANT_CLOSE_MS}ms ${ASSISTANT_MOTION_EASE}`,
		`opacity ${isOpen ? ASSISTANT_OPEN_MS : ASSISTANT_CLOSE_MS}ms ${ASSISTANT_MOTION_EASE}`,
		`width 300ms ${ASSISTANT_MOTION_EASE}`,
		`height 300ms ${ASSISTANT_MOTION_EASE}`,
		`right 300ms ${ASSISTANT_MOTION_EASE}`,
		`bottom 300ms ${ASSISTANT_MOTION_EASE}`,
		`border-radius 300ms ${ASSISTANT_MOTION_EASE}`,
		`visibility 0ms ${isOpen ? '0ms' : `${ASSISTANT_CLOSE_MS}ms`}`,
	].join(', ')

	// Emblem circle matches the input surface in dark mode; light mode
	// keeps the solid dark disc for contrast with the pale green mark.
	const assistantAvatarBg = theme.palette.mode === 'dark' ? 'background.level1' : 'text.primary'

	const showEmptyState = !isLoadingHistory && messages.length === 0

	// One row per prompt: a saved suggestion keeps its suggested slot and just
	// flips to the saved icon — no duplicate in a separate saved section.
	const promptRows = [
		...pinnedPrompts
			.filter((prompt) => !suggestedPrompts.includes(prompt.text))
			.map((prompt) => ({ id: `pin-${prompt.id}`, label: prompt.text, saved: true, pinId: prompt.id })),
		...suggestedPrompts.map((text) => {
			const pin = pinnedPrompts.find((prompt) => prompt.text === text)
			return { id: `sug-${text}`, label: text, saved: pin !== undefined, pinId: pin?.id }
		}),
	]

	const composer = (
		<ChatComposer
			contextLabel={contextLabel}
			floating={!showEmptyState}
			includePageContext={includePageContext}
			input={input}
			inputRef={inputRef}
			interimText={interimText}
			isExpanded={isExpanded}
			isListening={isListening}
			isLoading={isLoading}
			isMobile={isMobile}
			isReadingAloud={isReadingAloud}
			isSpeechLoading={isSpeechLoading}
			onInputChange={handleInputChange}
			onScrollToBottom={scrollToBottom}
			onStopStreaming={stopStreaming}
			onSubmit={handleSubmit}
			onToggleListening={handleToggleListening}
			onTogglePageContext={togglePageContext}
			showContextLabel={showContextLabel}
			showScrollDown={showScrollDown}
			speechSupported={isSupported}
		/>
	)

	return (
		<>
			{/* Docked launcher — a side modal's backdrop covers the nav trigger,
				so offer a floating reopen button in the same docked spot. */}
			{sideModalDocked && !isOpen ? (
				<Tooltip title="Ask the assistant">
					<IconButton
						aria-label="Open assistant"
						onClick={openPanel}
						sx={{
							display: { xs: 'none', sm: 'inline-flex' },
							position: 'fixed',
							right: dockedRight,
							bottom: ASSISTANT_WINDOW_OFFSET,
							zIndex: panelZIndex,
							width: 44,
							height: 44,
							borderRadius: '50%',
							bgcolor: assistantAvatarBg,
							boxShadow: '0 2px 8px rgba(0,0,0,0.16), 0 8px 24px rgba(0,0,0,0.16)',
							animation: 'dockLauncherIn 300ms cubic-bezier(0.22, 1, 0.36, 1) both',
							'@keyframes dockLauncherIn': {
								from: { opacity: 0, transform: 'scale(0.8)' },
								to: { opacity: 1, transform: 'scale(1)' },
							},
							'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
							'&:hover': { bgcolor: assistantAvatarBg },
						}}
					>
						<Box
							alt=""
							component="img"
							src="/assets/logo-emblem.svg"
							sx={{ width: 24, height: 'auto', display: 'block' }}
						/>
					</IconButton>
				</Tooltip>
			) : null}
			<Box
				aria-hidden={!isOpen}
				aria-label="Assistant"
				role="complementary"
				sx={{
					// On mobile this renders as a real page in document flow (the layout
					// hides the dashboard behind it) — fixed positioning under the iOS
					// keyboard produces gaps Safari never repaints.
					position: { xs: isOpen ? 'relative' : 'fixed', sm: 'fixed' },
					top: 'auto',
					right: { xs: 0, sm: dockedRight },
					bottom: { xs: 0, sm: ASSISTANT_WINDOW_OFFSET },
					width: { xs: '100%', sm: ASSISTANT_PANEL_WIDTH },
					height: {
						xs: '100dvh',
						sm: `min(${ASSISTANT_WINDOW_HEIGHT}px, calc(100vh - ${ASSISTANT_WINDOW_OFFSET * 2}px))`,
					},
					zIndex: panelZIndex,
					display: 'flex',
					flexDirection: 'column',
					bgcolor: 'background.default',
					borderRadius: { xs: 0, sm: 2 },
					boxShadow: {
						xs: 'none',
						// Dark mode: a whisper of a light ring so the near-black
						// panel edge stays visible, plus deeper shadows.
						sm:
							theme.palette.mode === 'dark'
								? '0 0 0 1px rgba(255,255,255,0.06), 0 4px 16px rgba(0,0,0,0.32), 0 12px 40px rgba(0,0,0,0.4)'
								: '0 4px 16px rgba(0,0,0,0.08), 0 12px 40px rgba(0,0,0,0.14)',
					},
					overflow: 'hidden',
					transformOrigin: 'center',
					transform: isOpen ? 'scale(1)' : `scale(${ASSISTANT_SCALE})`,
					opacity: isOpen ? 1 : 0,
					pointerEvents: isOpen ? 'auto' : 'none',
					visibility: isOpen ? 'visible' : 'hidden',
					transition: panelMotion,
					willChange: 'transform, opacity',
					'@media (prefers-reduced-motion: reduce)': {
						transition: 'none',
					},
					...(isExpanded
						? {
								width: {
									xs: '100%',
									sm: `min(800px, calc(100vw - ${ASSISTANT_WINDOW_OFFSET * 2}px))`,
								},
								height: { xs: 'auto', sm: `calc(100vh - ${ASSISTANT_WINDOW_OFFSET * 2}px)` },
							}
						: {}),
				}}
			>
				<ChatPanelHeader
					isExpanded={isExpanded}
					isMobile={isMobile}
					menuOpen={Boolean(menuAnchorEl)}
					onBackToHistory={() => {
						setShowHistory(true)
					}}
					onCancelDraft={() => {
						cancelDraftConversation()
						setShowHistory(true)
					}}
					onClose={handleClosePanel}
					onMinimize={closePanel}
					onNewConversation={() => {
						beginDraftConversation()
						setShowHistory(false)
					}}
					onOpenMenu={(anchorEl) => {
						setMenuAnchorEl(anchorEl)
					}}
					onToggleExpanded={handleToggleExpanded}
					view={showHistory ? 'history' : isDraftConversation ? 'draft' : 'chat'}
				/>

				<Menu
					anchorEl={menuAnchorEl}
					anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
					onClose={() => {
						setMenuAnchorEl(null)
					}}
					open={Boolean(menuAnchorEl)}
					slotProps={{ paper: { sx: { minWidth: 190 } } }}
					transformOrigin={{ horizontal: 'right', vertical: 'top' }}
				>
					<MenuItem
						onClick={() => {
							setMenuAnchorEl(null)
							beginDraftConversation()
							setShowHistory(false)
						}}
					>
						<ListItemIcon>
							<PlusIcon size={18} />
						</ListItemIcon>
						<ListItemText>New conversation</ListItemText>
					</MenuItem>
					<MenuItem
						disabled={!messages.length}
						onClick={() => {
							setMenuAnchorEl(null)
							handleCopyMarkdown()
						}}
					>
						<ListItemIcon>
							<CopyIcon size={18} />
						</ListItemIcon>
						<ListItemText>Copy as markdown</ListItemText>
					</MenuItem>
					<MenuItem
						disabled={!threadId}
						onClick={() => {
							setMenuAnchorEl(null)
							if (threadId) setThreadToDelete(threadId)
						}}
						sx={{ color: 'error.main' }}
					>
						<ListItemIcon sx={{ color: 'inherit' }}>
							<TrashIcon size={18} />
						</ListItemIcon>
						<ListItemText>Delete chat</ListItemText>
					</MenuItem>
				</Menu>

				<Menu
					anchorEl={threadMenu?.anchorEl ?? null}
					anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
					onClose={() => {
						setThreadMenu(null)
					}}
					open={Boolean(threadMenu)}
					transformOrigin={{ horizontal: 'right', vertical: 'top' }}
				>
					<MenuItem
						onClick={() => {
							if (threadMenu) setThreadToDelete(threadMenu.threadId)
							setThreadMenu(null)
						}}
						sx={{ color: 'error.main' }}
					>
						<ListItemIcon sx={{ color: 'inherit' }}>
							<TrashIcon size={16} />
						</ListItemIcon>
						<ListItemText>Delete</ListItemText>
					</MenuItem>
				</Menu>

				{error ? (
					<Alert severity="error" sx={{ borderRadius: 0 }}>
						{error}
					</Alert>
				) : null}

				{micError ? (
					<Alert onClose={clearError} severity="warning" sx={{ borderRadius: 0 }}>
						{micError}
					</Alert>
				) : null}

				{showHistory ? (
					<ChatHistoryList
						currentThreadId={threadId}
						expanded={isExpanded}
						menuThreadId={threadMenu?.threadId ?? null}
						onOpenMenu={(anchorEl, id) => {
							setThreadMenu({ anchorEl, threadId: id })
						}}
						onSelect={(id) => {
							void selectThread(id)
							setShowHistory(false)
						}}
						threads={threads}
					/>
				) : (
					<>
						<Box
							onScroll={handleListScroll}
							ref={listRef}
							role="log"
							sx={{
								flex: 1,
								minHeight: 0,
								overflowY: 'auto',
								overscrollBehavior: 'contain',
								py: 2,
								// Clearance under the floating composer + soft fade so
								// content dissolves under it. Not needed in the empty state —
								// the composer renders in flow there.
								...(showEmptyState
									? {}
									: {
											pb: { xs: '200px', sm: '140px' },
											maskImage: 'linear-gradient(to bottom, black calc(100% - 48px), transparent)',
											WebkitMaskImage: 'linear-gradient(to bottom, black calc(100% - 48px), transparent)',
										}),
							}}
						>
							<Box
								sx={{
									...(isExpanded ? { maxWidth: 760, mx: 'auto' } : {}),
									// New chats center the greeting + composer vertically.
									...(showEmptyState ? { display: 'flex', flexDirection: 'column', minHeight: '100%' } : {}),
								}}
							>
								{isLoadingHistory ? (
									<Stack sx={{ alignItems: 'center', py: 6 }}>
										<ThinkingIndicator label="Loading conversation…" />
									</Stack>
								) : showEmptyState ? (
									// my:auto (not justifyContent) so an overflowing block still
									// scrolls from the top instead of clipping above the fold.
									<Stack spacing={2.5} sx={{ my: 'auto', px: 2, py: 2 }}>
										<Box
											sx={{
												mx: 'auto',
												animation: 'emptyStateMarkIn 500ms cubic-bezier(0.22, 1, 0.36, 1) both',
												'@keyframes emptyStateMarkIn': {
													from: { opacity: 0, transform: 'translateY(12px)', filter: 'blur(3px)' },
													to: { opacity: 1, transform: 'translateY(0)', filter: 'blur(0)' },
												},
												'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
											}}
										>
											<AssistantEmblem emblemWidth={28} size={48} />
										</Box>
										<Typography fontWeight={500} sx={{ textAlign: 'center' }} variant="subtitle1">
											How can I help{firstName ? `, ${firstName}` : ''}?
										</Typography>
										{composer}
										{promptRows.length > 0 ? (
											<Stack spacing={0} sx={{ alignSelf: 'stretch' }}>
												{promptRows.map((row, index) => (
													<PromptRow
														key={row.id}
														disabled={isLoading}
														index={index}
														label={row.label}
														onSelect={handleSuggestedPrompt}
														onToggleSave={() => {
															row.saved && row.pinId ? handleUnpinPrompt(row.pinId) : handlePinPrompt(row.label)
														}}
														saved={row.saved}
													/>
												))}
											</Stack>
										) : null}
									</Stack>
								) : (
									<>
										{showNavNudge ? (
											<Box
												sx={{
													alignItems: 'center',
													bgcolor: 'action.hover',
													borderRadius: '10px',
													display: 'flex',
													gap: 1,
													mb: 1,
													mx: 2,
													px: 1.25,
													py: 0.75,
												}}
											>
												<Box component="span" sx={{ color: 'text.secondary', display: 'inline-flex', flexShrink: 0 }}>
													<InfoIcon size={14} />
												</Box>
												<Typography color="text.secondary" sx={{ flex: 1, lineHeight: 1.4 }} variant="caption">
													This thread started on a different page. New questions will use this page&apos;s context
													instead.
												</Typography>
												<Button
													color="inherit"
													onClick={() => void clearThread()}
													size="small"
													sx={{
														flexShrink: 0,
														fontSize: '0.75rem',
														minWidth: 0,
														px: 1,
														textTransform: 'none',
														whiteSpace: 'nowrap',
													}}
												>
													New thread
												</Button>
												<IconButton
													aria-label="Dismiss"
													onClick={() => {
														setDismissedNavNudgeKey(navNudgeKey)
													}}
													size="small"
													sx={{ color: 'text.secondary', flexShrink: 0, height: 22, width: 22 }}
												>
													<XIcon size={12} weight="bold" />
												</IconButton>
											</Box>
										) : null}
										{messages.map((message) => (
											<Box
												data-mid={message.id}
												key={message.id}
												sx={{
													// transitions.dev "toast" open: 350ms, 16px rise,
													// 0.97 scale, 2px cross-blur — plays once on send.
													'&.msg-sent': {
														animation: 'msgSentIn 350ms cubic-bezier(0.22, 1, 0.36, 1) both',
														// Grow from the composer side — user bubbles are right-aligned.
														transformOrigin: 'bottom right',
													},
													'@keyframes msgSentIn': {
														from: { opacity: 0, transform: 'translateY(16px) scale(0.97)', filter: 'blur(2px)' },
														to: { opacity: 1, transform: 'translateY(0) scale(1)', filter: 'blur(0)' },
													},
													'@media (prefers-reduced-motion: reduce)': {
														'&.msg-sent': { animation: 'none' },
													},
												}}
											>
												<ChatMessageBubble message={message} />
											</Box>
										))}
									</>
								)}
								{followUps.length > 0 ? (
									<Stack direction="row" flexWrap="wrap" spacing={0.5} sx={{ px: 2, py: 1 }} useFlexGap>
										{followUps.map((suggestion) => (
											<Chip
												key={suggestion}
												clickable
												disabled={isLoading}
												label={suggestion}
												onClick={() => {
													handleSuggestedPrompt(suggestion)
												}}
												size="small"
												sx={{ ...promptTextSx }}
												variant="outlined"
											/>
										))}
									</Stack>
								) : null}
								<div ref={bottomRef} />
							</Box>
							{/* Scroll room while an answer streams in — lets the just-sent
								question pin to the top of the viewport even when the
								response is still shorter than the panel. */}
							{isLoading ? <Box sx={{ flexShrink: 0, height: 'calc(100% - 48px)' }} /> : null}
						</Box>

						{!showEmptyState ? composer : null}
					</>
				)}
				<Dialog
					open={threadToDelete !== null}
					onClose={() => {
						if (!isDeletingThread) setThreadToDelete(null)
					}}
					maxWidth="xs"
					fullWidth
				>
					<DialogTitle>Delete conversation?</DialogTitle>
					<DialogContent>
						<DialogContentText>
							This removes the conversation from your history. This can&apos;t be undone.
						</DialogContentText>
					</DialogContent>
					<DialogActions sx={{ px: 3, pb: 2 }}>
						<Button
							disabled={isDeletingThread}
							onClick={() => {
								setThreadToDelete(null)
							}}
						>
							Cancel
						</Button>
						<Button
							color="error"
							disabled={isDeletingThread}
							onClick={() => void handleConfirmDeleteThread()}
							variant="contained"
						>
							Delete
						</Button>
					</DialogActions>
				</Dialog>
			</Box>
		</>
	)
}
